import { ART_PAL, shade, tint, type ArtTheme } from './covers/kit';

/*
 * The in-game token: a round orange token with a geometric four-point "spark" mark — NOT a $/€/gold coin.
 * Soft volume (light top-left), a rim, a recessed face and a bottom edge; readable down to 12 px.
 * Paints are CSS values (style="…"), so inline copies can follow the theme through var(--orange);
 * file exports use the hex palette.
 */
export interface TokenPaint {
  hi: string;
  base: string;
  lo: string;
  rim: string;
  edge: string;
  face: string;
  mark: string;
}

/** Theme-following paints for inline SVG (uses CSS variables). */
export const TOKEN_CSS: TokenPaint = {
  hi: 'color-mix(in srgb, #fff 45%, var(--orange))',
  base: 'var(--orange)',
  lo: 'color-mix(in srgb, #000 30%, var(--orange))',
  rim: 'color-mix(in srgb, #000 42%, var(--orange))',
  edge: 'color-mix(in srgb, #000 50%, var(--orange))',
  face: 'color-mix(in srgb, #000 12%, var(--orange))',
  mark: 'color-mix(in srgb, #fff 82%, var(--orange))',
};

export function tokenPaint(theme: ArtTheme): TokenPaint {
  const o = ART_PAL[theme].orange;
  return { hi: tint(o, 0.45), base: o, lo: shade(o, 0.3), rim: shade(o, 0.42), edge: shade(o, 0.5), face: shade(o, 0.12), mark: tint(o, 0.82) };
}

const st = (prop: 'fill' | 'stop-color' | 'stroke', v: string) => `style="${prop}:${v}"`;

/** Mark: four-point spark (rounded diamond star) — geometric, no letters or currency signs. */
const SPARK =
  'M64 30C66.5 50 72 58 90 64C72 70 66.5 78 64 98C61.5 78 56 70 38 64C56 58 61.5 50 64 30Z';

/**
 * One token face-on, viewBox 0 0 128 128 (content inside r≈58).
 * `small` (icons under ~24 px): no recessed face ring, a bigger mark — stays readable at 12 px.
 */
export function tokenMarkup(c: TokenPaint, p: string, cx = 64, cy = 64, k = 1, small = false): string {
  const tr = cx === 64 && cy === 64 && k === 1 ? '' : ` transform="translate(${cx - 64 * k} ${cy - 64 * k}) scale(${k})"`;
  return (
    `<defs>` +
    `<linearGradient id="${p}b" x1=".2" y1="0" x2=".7" y2="1"><stop offset="0" ${st('stop-color', c.hi)}/><stop offset=".4" ${st('stop-color', c.base)}/><stop offset=".8" ${st('stop-color', c.face)}/><stop offset="1" ${st('stop-color', c.lo)}/></linearGradient>` +
    `<linearGradient id="${p}f" x1=".8" y1="1" x2=".25" y2="0"><stop offset="0" ${st('stop-color', c.hi)}/><stop offset=".5" ${st('stop-color', c.base)}/><stop offset="1" ${st('stop-color', c.face)}/></linearGradient>` +
    `</defs>` +
    `<g${tr}>` +
    // edge (thickness) peeking below
    `<circle cx="64" cy="69" r="56" ${st('fill', c.edge)}/>` +
    `<circle cx="64" cy="64" r="56" fill="url(#${p}b)" ${st('stroke', c.rim)} stroke-width="3"/>` +
    // recessed face (inverse gradient reads as a dip)
    (small
      ? `<path d="${SPARK}" transform="translate(64 64) scale(1.3) translate(-64 -64)" ${st('fill', c.mark)}/>`
      : `<circle cx="64" cy="64" r="41" fill="url(#${p}f)" ${st('stroke', c.rim)} stroke-width="2.5" stroke-opacity=".7"/>` +
        `<path d="${SPARK}" ${st('fill', c.mark)}/>` +
        `<path d="${SPARK}" transform="translate(64 64) scale(.36) translate(-64 -64)" ${st('fill', c.base)} fill-opacity=".55"/>`) +
    // highlight top-left
    `<path d="M24 50C28 32 44 18 62 15" fill="none" stroke="#fff" stroke-opacity=".55" stroke-width="6" stroke-linecap="round"/>` +
    `</g>`
  );
}

/** A small stack of tokens (3/4 view) + one leaning in front, viewBox 0 0 128 128. */
export function tokenStackMarkup(c: TokenPaint, p: string): string {
  const disc = (y: number, i: number) =>
    `<ellipse cx="58" cy="${y + 7}" rx="40" ry="14" ${st('fill', c.edge)}/>` +
    `<rect x="18" y="${y}" width="80" height="7" ${st('fill', c.lo)}/>` +
    `<ellipse cx="58" cy="${y}" rx="40" ry="14" fill="url(#${p}t)" ${st('stroke', c.rim)} stroke-width="2"/>` +
    `<ellipse cx="58" cy="${y}" rx="27" ry="9" fill="none" ${st('stroke', c.rim)} stroke-opacity=".5" stroke-width="2"/>` +
    (i === 3 ? `<path d="M58 ${y - 7}C59 ${y - 2} 61 ${y - 1} 68 ${y}C61 ${y + 1} 59 ${y + 2} 58 ${y + 7}C57 ${y + 2} 55 ${y + 1} 48 ${y}C55 ${y - 1} 57 ${y - 2} 58 ${y - 7}Z" ${st('fill', c.mark)}/>` : '');
  return (
    `<defs><linearGradient id="${p}t" x1="0" y1="0" x2="1" y2="1"><stop offset="0" ${st('stop-color', c.hi)}/><stop offset=".5" ${st('stop-color', c.base)}/><stop offset="1" ${st('stop-color', c.face)}/></linearGradient>` +
    `<radialGradient id="${p}s"><stop offset="0" stop-color="#000" stop-opacity=".35"/><stop offset="1" stop-color="#000" stop-opacity="0"/></radialGradient></defs>` +
    `<ellipse cx="64" cy="112" rx="56" ry="9" fill="url(#${p}s)"/>` +
    disc(96, 0) +
    disc(84, 1) +
    disc(72, 2) +
    disc(60, 3) +
    tokenMarkup(c, `${p}f`, 96, 84, 0.36)
  );
}

export function tokenSvg(theme: ArtTheme, prefix = 'tk'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="128" height="128">${tokenMarkup(tokenPaint(theme), prefix)}</svg>`;
}
export function tokenStackSvg(theme: ArtTheme, prefix = 'ts'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="128" height="128">${tokenStackMarkup(tokenPaint(theme), prefix)}</svg>`;
}
